import { Controller } from '@hotwired/stimulus';
import { createRoot } from 'react-dom/client';
import { createElement } from 'react';
import { getComponent } from '../dashboard/registry';

/**
 * Thin Stimulus controller — fetches credentials from API,
 * mounts/unmounts React components.
 *
 * Credentials are NOT in HTML data attributes (security).
 * Only equity history (non-sensitive) is embedded in the page.
 */
export default class LiveDashboardController extends Controller {
  static values = {
    equityHistory: { type: String, default: '[]' },
    marketSnapshots: { type: String, default: '' },
  };

  async connect() {
    this.roots = [];

    // Fetch credentials from authenticated API endpoint
    let config = {};
    try {
      const res = await fetch('/api/ctrader/credentials');
      if (res.ok) {
        const data = await res.json();
        config = {
          clientId: data.clientId,
          clientSecret: data.clientSecret,
          accessToken: data.accessToken,
          accountId: data.accountId,
          isDemo: data.isDemo,
        };
      }
    } catch (e) {
      console.error('[dashboard] Failed to fetch credentials:', e);
    }

    let equityHistory = [];
    try { equityHistory = JSON.parse(this.equityHistoryValue); } catch { /* ignore */ }

    // Parse market snapshots if present (for /market page)
    let pageData = {};
    if (this.marketSnapshotsValue) {
      try { pageData = JSON.parse(this.marketSnapshotsValue); } catch { /* ignore */ }
    }

    this.mountComponents(config, equityHistory, pageData);
  }

  disconnect() {
    for (const root of this.roots) {
      root.unmount();
    }
    this.roots = [];
  }

  mountComponents(config, equityHistory, pageData) {
    const mounts = this.element.querySelectorAll('[data-react-component]');

    if (mounts.length > 0) {
      for (const el of mounts) {
        this.mountOne(el, el.dataset.reactComponent, el.dataset.reactProps, config, equityHistory, pageData);
      }
    } else {
      const rootEl = this.element.querySelector('[data-react-root]');
      if (rootEl) {
        this.mountOne(rootEl, 'LiveDashboard', null, config, equityHistory, pageData);
      }
    }
  }

  mountOne(el, componentName, propsJson, config, equityHistory, pageData) {
    const Component = getComponent(componentName);
    if (!Component) {
      console.warn(`[react-mount] Unknown component: ${componentName}`);
      return;
    }

    let extraProps = {};
    if (propsJson) {
      try { extraProps = JSON.parse(propsJson); } catch { /* ignore */ }
    }

    const root = createRoot(el);
    root.render(createElement(Component, { config, equityHistory, ...pageData, ...extraProps }));
    this.roots.push(root);
  }
}
